import { memo } from "react";

import Box from "@mui/material/Box";
import {
  DatePicker,
  DatePickerSlotsComponentsProps,
} from "@mui/x-date-pickers";
import { BaseNonStaticPickerProps } from "@mui/x-date-pickers/internals";
import { BaseNonRangeNonStaticPickerProps } from "@mui/x-date-pickers/internals/models/props/basePickerProps";
import { UsePickerLayoutProps } from "@mui/x-date-pickers/internals/hooks/usePicker/usePickerLayoutProps";
import { UsePickerValueBaseProps } from "@mui/x-date-pickers/internals/hooks/usePicker/usePickerValue.types";
import { FormControl } from "@mui/material";
import FormLabel from "@mui/joy/FormLabel";

/**
 * Props for Datepicker Component.
 * @typedef
 * @name DatepickerProps
 * @kind variable
 */
type DatepickerProps = {
  /**
   * (optional)
   * Standard HTML Attribute, for classes.
   * @property
   * @name className
   * @type {string | undefined}
   */
  className?: string;
  /**
   * (optional)
   * Id for the Datepicker Component.
   * @property
   * @name id
   * @type {string | undefined}
   */
  id?: string;
  /**
   * If `true`, shows the validation message.
   * @default false
   * @property
   * @name hasError
   * @type {boolean}
   */
  hasError: boolean;
  /**
   * Validation message.
   * @property
   * @name validationMessage
   * @type {string}
   */
  validationMessage: string;
  /**
   * Label Id for the Datepicker Component.
   * @property
   * @name labelId
   * @type {string}
   */
  labelId: string;
  /**
   * (optional)
   * Standard HTML Attribute, for classes.
   * @property
   * @name labelClassName
   * @type {string | undefined}
   */
  labelClassName?: string;
  /**
   * Props used for each component slot.
   * @property
   * @name slotProps
   * @type {DatePickerSlotsComponentsProps<unknown> | undefined}
   */
  slotProps: DatePickerSlotsComponentsProps<unknown> | undefined;
  /**
   * (optional)
   * Maximal selectable date.
   * @property
   * @name maxDate
   * @type {unknown}
   */
  maxDate?: unknown;
  /**
   * (optional)
   * Minimal selectable date.
   * @property
   * @name minDate
   * @type {unknown}
   */
  minDate?: unknown;
  /**
   * (optional)
   * If `true`, shows the calendar icon.
   * @property
   * @name showCalendarIcon
   * @type {boolean | undefined}
   */
  showCalendarIcon?: boolean;
  /**
   * (optional)
   * If `true`, opens the calendar.
   * @property
   * @name isOpen
   * @type {boolean | undefined}
   */
  isOpen?: boolean;
  /**
   * (optional)
   * Height of the input.
   * @property
   * @name height
   * @type {string | undefined}
   */
  height?: string;
  /**
   * (optional)
   * If `true`, disables the future on the calendar.
   * @property
   * @name disableFuture
   * @type {boolean | undefined}
   */
  disableFuture?: boolean;
  /**
   * (optional)
   * If `true`, closes the calendar when a day is selected.
   * @property
   * @name closeOnSelect
   * @type {boolean | undefined}
   */
  closeOnSelect?: boolean;
  /**
   * (optional)
   * If `true`, disables the picker.
   * @property
   * @name disableOpenPicker
   * @type {boolean | undefined}
   */
  disableOpenPicker?: boolean;
  /**
   * (optional)
   * Callback fired when the popup requests to be opened.
   * @property
   * @name onOpen
   * @type {function}
   */
  onOpen?: () => void;
  /**
   * (optional)
   * Callback fired when the popup requests to be closed.
   * @property
   * @name onClose
   * @type {function}
   */
  onClose?: () => void;
} & BaseNonStaticPickerProps &
  BaseNonRangeNonStaticPickerProps &
  UsePickerLayoutProps &
  UsePickerValueBaseProps<unknown, unknown>;

/**
 * Memoized Datepicker Component.
 * @constant
 * @name DatepickerComponent
 * @kind variable
 * @type {React.NamedExoticComponent<DatepickerProps>}
 */
const DatepickerComponent: React.NamedExoticComponent<DatepickerProps> = memo(
  /**
   * Datepicker Component.
   * @function
   * @name DatepickerComponent
   * @kind function
   * @memberof DatepickerComponent
   * @param {DatepickerProps} props Props for Datepicker Component.
   * @returns {JSX.Element} Datepicker Component.
   */
  function DatepickerComponent(props: DatepickerProps): JSX.Element {
    return (
      <Box className="w-full" id={props.id}>
        <FormControl fullWidth>
          <FormLabel
            className={`ml-2 mb-1 text-m font-normal text-neutral-500 ${props.labelClassName}`}
            id={props.labelId}
            htmlFor={props.id}
          >
            {props.label}
          </FormLabel>
          <DatePicker
            onOpen={props.onOpen}
            onClose={props.onClose}
            disableFuture={props.disableFuture}
            closeOnSelect={props.closeOnSelect}
            value={props.value || null}
            disabled={props.disabled}
            maxDate={props.maxDate}
            minDate={props.minDate}
            readOnly={props.readOnly}
            className={"datepicker " + props.className}
            format={props.format}
            formatDensity={props.formatDensity}
            onChange={props.onChange}
            slotProps={props.slotProps}
            disableOpenPicker={props.disableOpenPicker}
            open={props.isOpen}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: props.height || "48px",
                // adding a different border radius to the datepicker component and color if hasError is true
                "& fieldset": {
                  borderRadius: "10px", //input border is 10px
                  borderColor: props.hasError ? "rgb(239, 68, 68)" : "#AFAFAE",
                },
                "&:hover fieldset": {
                  borderColor: props.hasError ? "rgb(239, 68, 68)" : "#AFAFAE",
                },
                // set to display, or not, the calendar button
                "& .MuiInputAdornment-root": {
                  display: props.showCalendarIcon ? "" : "none",
                },
              },
              // changes only the placeholder value color
              "input.MuiInputBase-input.MuiOutlinedInput-input::placeholder": {
                color: "#C6C6C6",
              },
              "& .MuiOutlinedInput-root.Mui-disabled": {
                backgroundColor: "rgb(249 249 249 / 1)",
                borderRadius: "10px",
                "& fieldset": {
                  borderColor: "rgb(230 230 230 / 1)",
                  boxShadow: "1px 1px 8px 1px rgb(230 230 230 / .5)",
                },
              },
              "& .MuiPickersPopper-root .MuiPaper-root": {
                maxHeight: "400px",
                overflowY: "auto",
              },
              "& .MuiPickersPopper-root .MuiPaper-root::-webkit-scrollbar": {
                width: "6px",
              },
              "& .MuiPickersPopper-root .MuiPaper-root::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "3px",
              },
              "& .MuiPickersPopper-root .MuiPaper-root::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "3px",
              },
              "& .MuiPickersPopper-root .MuiPaper-root::-webkit-scrollbar-thumb:hover": {
                background: "#a8a8a8",
              },
            }}
          />

          {props.hasError && (
            <div className="ml-2 mt-1 text-xs text-red-400">
              {props.validationMessage}
            </div>
          )}
        </FormControl>
      </Box>
    );
  }
);

export default DatepickerComponent;